/**
 * 既存プレーン／押し出しへの設置スナップ。
 * ビューグリッドを空間格子とみなし、面∩格子線・辺∩格子面・頂点へ吸う。
 */

import { STRIP_FRAME_PAD } from './brace-snap';
import {
  asPlaneGeometry,
  braceExtrudeOffset,
  bracePolyline,
  fillSideFace,
  fillSideFaceHoles,
  nearestPathFace,
  pathFaceHoles,
  pathLogicalFace,
  planeBasis,
  planeSlab,
  tessellatePlanePolygon,
  tessellateSlab,
  vec,
  type PathFace,
} from './geometry';
import { pointInPolygon, type Point2D } from './holes';
import type { BraceGeometry, PlaneGeometry, Vec3 } from './types';
import {
  type GridAxis,
  VIEW_GRID_TILE,
  gridCoords,
  gridLevels,
  intersectGridLinePlane,
  intersectSegmentGridPlane,
} from './view-grid';

export interface PlaneSnapLine {
  a: Vec3;
  b: Vec3;
}

export interface SnapFace {
  points: Vec3[];
  holes: Vec3[][];
  normal: Vec3;
}

export interface SnapSolid {
  vertices: Vec3[];
  edges: { a: Vec3; b: Vec3 }[];
  faces: SnapFace[];
}

const POINT_EPS = 1e-6;
const FACE_LINE_NEIGHBORHOOD = 2;
const GRID_AXES: GridAxis[] = ['x', 'y', 'z'];
const GRID_LINE_PAIRS: [GridAxis, GridAxis][] = [
  ['x', 'z'],
  ['x', 'y'],
  ['y', 'z'],
];

/** 緑格子は照準のこの半径だけ描く。巨大プレーン全面は出さない。 */
export const PLANE_SNAP_FRAME_RADIUS = 12;

function snapStep(snap: number): number {
  return snap > 0 ? snap : VIEW_GRID_TILE;
}

function samePoint(a: Vec3, b: Vec3): boolean {
  return vec.length(vec.sub(a, b)) < POINT_EPS;
}

function pushUnique(points: Vec3[], point: Vec3): void {
  if (points.some((existing) => samePoint(existing, point))) return;
  points.push(point);
}

function pushUniqueEdge(edges: { a: Vec3; b: Vec3 }[], a: Vec3, b: Vec3): void {
  if (samePoint(a, b)) return;
  if (
    edges.some(
      (edge) =>
        (samePoint(edge.a, a) && samePoint(edge.b, b)) || (samePoint(edge.a, b) && samePoint(edge.b, a))
    )
  ) {
    return;
  }
  edges.push({ a, b });
}

function addRing(solid: SnapSolid, ring: Vec3[]): void {
  for (let index = 0; index < ring.length; index++) {
    pushUnique(solid.vertices, ring[index]);
    pushUniqueEdge(solid.edges, ring[index], ring[(index + 1) % ring.length]);
  }
}

function addFace(solid: SnapSolid, points: Vec3[], holes: Vec3[][], normal: Vec3): void {
  if (points.length < 3) return;
  const unit = vec.normalize(normal);
  if (vec.length(unit) < 1e-9) return;
  addRing(solid, points);
  for (const hole of holes) {
    if (hole.length >= 3) addRing(solid, hole);
  }
  solid.faces.push({ points, holes, normal: unit });
}

function emptySolid(): SnapSolid {
  return { vertices: [], edges: [], faces: [] };
}

function projectFace(point: Vec3, origin: Vec3, tangent: Vec3, bitangent: Vec3): Point2D {
  const delta = vec.sub(point, origin);
  return { x: vec.dot(delta, tangent), y: vec.dot(delta, bitangent) };
}

function pointOnSnapFace(point: Vec3, face: SnapFace): boolean {
  const origin = face.points[0];
  if (!origin) return false;
  if (Math.abs(vec.dot(vec.sub(point, origin), face.normal)) > 1e-4) return false;
  const { tangent, bitangent } = planeBasis(face.normal);
  const uv = projectFace(point, origin, tangent, bitangent);
  const outer = face.points.map((entry) => projectFace(entry, origin, tangent, bitangent));
  if (!pointInPolygon(uv, outer, true)) return false;
  for (const hole of face.holes) {
    if (hole.length < 3) continue;
    const ring = hole.map((entry) => projectFace(entry, origin, tangent, bitangent));
    if (pointInPolygon(uv, ring, true)) {
      let onEdge = false;
      for (let index = 0; index < hole.length; index++) {
        const a = hole[index];
        const b = hole[(index + 1) % hole.length];
        const ab = vec.sub(b, a);
        const length = vec.length(ab);
        if (length < POINT_EPS) continue;
        const t = vec.dot(vec.sub(point, a), ab) / (length * length);
        if (t < -1e-6 || t > 1 + 1e-6) continue;
        const closest = vec.add(a, vec.scale(ab, Math.min(1, Math.max(0, t))));
        if (vec.length(vec.sub(point, closest)) < 1e-4) {
          onEdge = true;
          break;
        }
      }
      if (!onEdge) return false;
    }
  }
  return true;
}

/** fill スラブの面・辺・頂点。厚み 0 は輪郭 1 枚。 */
export function fillSnapSolid(geometry: BraceGeometry | PlaneGeometry): SnapSolid | null {
  const plane = asPlaneGeometry(geometry);
  const slab = planeSlab(plane);
  if (!slab) return null;
  const solid = emptySolid();

  if (slab.paperThin) {
    addFace(solid, tessellatePlanePolygon(plane), plane.holes ?? [], slab.normal);
    return solid.faces.length > 0 ? solid : null;
  }

  const tessellated = tessellateSlab(plane);
  const start = tessellated?.slab.start ?? slab.start;
  const holes = tessellated?.holeRings ?? [];
  const end = start.map((point) => vec.add(point, slab.offset));
  const endHoles = holes.map((ring) => ring.map((point) => vec.add(point, slab.offset)));
  addFace(solid, start, holes, slab.normal);
  addFace(solid, end, endHoles, vec.scale(slab.normal, -1));

  for (let index = 0; index < start.length; index++) {
    const a = start[index];
    const b = start[(index + 1) % start.length];
    const c = vec.add(b, slab.offset);
    const d = vec.add(a, slab.offset);
    const normal = vec.normalize(vec.cross(vec.sub(b, a), slab.offset));
    if (vec.length(normal) < 1e-9) continue;
    addFace(solid, [a, b, c, d], [], normal);
  }

  for (const ring of holes) {
    for (const point of ring) {
      pushUnique(solid.vertices, vec.add(point, slab.offset));
      pushUniqueEdge(solid.edges, point, vec.add(point, slab.offset));
    }
  }

  for (let index = 0; index < plane.points.length; index++) {
    const face = fillSideFace(plane, index);
    if (!face) continue;
    const sideHoles = fillSideFaceHoles(plane, face);
    if (sideHoles.length === 0) continue;
    addFace(solid, face.points, sideHoles, face.normal);
  }

  return solid.faces.length > 0 ? solid : null;
}

/** 押し出し帯の面・辺・頂点。 */
export function pathExtrudeSnapSolid(geometry: BraceGeometry): SnapSolid | null {
  if (vec.length(braceExtrudeOffset(geometry)) < 1e-9) return null;
  const { points, closed } = bracePolyline(geometry);
  const count = closed ? points.length : points.length - 1;
  const solid = emptySolid();
  for (let index = 0; index < count; index++) {
    const face = pathLogicalFace(geometry, index);
    if (!face) continue;
    addFace(solid, face.points, pathFaceHoles(geometry, face), face.normal);
  }
  return solid.faces.length > 0 ? solid : null;
}

function faceSnapSolid(face: PathFace): SnapSolid {
  const solid = emptySolid();
  addFace(solid, face.points, [], face.normal);
  return solid;
}


function boundsOf(points: Vec3[], yaw: number): { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } } | null {
  if (points.length === 0) return null;
  const first = gridCoords(points[0], yaw);
  const min = { ...first };
  const max = { ...first };
  for (let index = 1; index < points.length; index++) {
    const coords = gridCoords(points[index], yaw);
    if (coords.x < min.x) min.x = coords.x;
    if (coords.y < min.y) min.y = coords.y;
    if (coords.z < min.z) min.z = coords.z;
    if (coords.x > max.x) max.x = coords.x;
    if (coords.y > max.y) max.y = coords.y;
    if (coords.z > max.z) max.z = coords.z;
  }
  return { min, max };
}

function expandBounds(
  bounds: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } },
  pad: number
) {
  return {
    min: { x: bounds.min.x - pad, y: bounds.min.y - pad, z: bounds.min.z - pad },
    max: { x: bounds.max.x + pad, y: bounds.max.y + pad, z: bounds.max.z + pad },
  };
}

function clipBounds(
  a: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } },
  b: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } }
) {
  const min = {
    x: Math.max(a.min.x, b.min.x),
    y: Math.max(a.min.y, b.min.y),
    z: Math.max(a.min.z, b.min.z),
  };
  const max = {
    x: Math.min(a.max.x, b.max.x),
    y: Math.min(a.max.y, b.max.y),
    z: Math.min(a.max.z, b.max.z),
  };
  if (max.x < min.x || max.y < min.y || max.z < min.z) return null;
  return { min, max };
}

function nearestOf(point: Vec3, candidates: Vec3[]): Vec3 {
  let best = candidates[0];
  let bestDist = vec.length(vec.sub(point, best));
  for (let index = 1; index < candidates.length; index++) {
    const candidate = candidates[index];
    const dist = vec.length(vec.sub(point, candidate));
    if (dist < bestDist) {
      best = candidate;
      bestDist = dist;
    }
  }
  return best;
}

function collectEdgeHits(
  solid: SnapSolid,
  snap: number,
  yaw: number,
  window: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } } | null,
  candidates: Vec3[]
): void {
  const step = snapStep(snap);
  for (const edge of solid.edges) {
    const a = gridCoords(edge.a, yaw);
    const b = gridCoords(edge.b, yaw);
    for (const axis of GRID_AXES) {
      let lo = Math.min(a[axis], b[axis]);
      let hi = Math.max(a[axis], b[axis]);
      if (window) {
        lo = Math.max(lo, window.min[axis]);
        hi = Math.min(hi, window.max[axis]);
      }
      if (hi < lo) continue;
      for (const level of gridLevels(lo, hi, step)) {
        const hit = intersectSegmentGridPlane(edge.a, edge.b, axis, level, yaw);
        if (hit) pushUnique(candidates, hit);
      }
    }
  }
}

function collectFaceLineHits(
  faces: SnapFace[],
  snap: number,
  yaw: number,
  window: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } },
  candidates: Vec3[]
): void {
  const step = snapStep(snap);
  for (const face of faces) {
    const origin = face.points[0];
    if (!origin) continue;
    const faceBounds = boundsOf(face.points, yaw);
    if (!faceBounds) continue;
    const box = clipBounds(faceBounds, window);
    if (!box) continue;
    for (const [axisA, axisB] of GRID_LINE_PAIRS) {
      for (const levelA of gridLevels(box.min[axisA], box.max[axisA], step)) {
        for (const levelB of gridLevels(box.min[axisB], box.max[axisB], step)) {
          const hit = intersectGridLinePlane(axisA, levelA, axisB, levelB, origin, face.normal, yaw);
          if (!hit || !pointOnSnapFace(hit, face)) continue;
          pushUnique(candidates, hit);
        }
      }
    }
  }
}

function snapSolidCandidates(
  solid: SnapSolid,
  point: Vec3,
  snap: number,
  yaw: number,
  neighborhood?: number
): Vec3[] {
  const candidates: Vec3[] = [];
  for (const vertex of solid.vertices) pushUnique(candidates, vertex);
  const faceBounds = boundsOf(
    solid.faces.flatMap((face) => face.points),
    yaw
  );
  const pad = neighborhood ?? Infinity;
  const focusBox =
    faceBounds && Number.isFinite(pad) && pad > 0
      ? expandBounds({ min: gridCoords(point, yaw), max: gridCoords(point, yaw) }, pad)
      : faceBounds;
  const window = faceBounds && focusBox ? clipBounds(faceBounds, focusBox) : faceBounds;
  collectEdgeHits(solid, snap, yaw, window, candidates);
  if (window) collectFaceLineHits(solid.faces, snap, yaw, window, candidates);
  return candidates;
}

function snapToSolid(solid: SnapSolid | null, point: Vec3, snap: number, yaw: number): Vec3 {
  if (!solid) return point;
  const radius = FACE_LINE_NEIGHBORHOOD * snapStep(snap);
  const candidates = snapSolidCandidates(solid, point, snap, yaw, radius);
  return candidates.length === 0 ? point : nearestOf(point, candidates);
}

export function planeSnapPoints(geometry: PlaneGeometry, snap: number, yaw = 0): Vec3[] {
  const solid = fillSnapSolid(geometry);
  if (!solid) return [];
  return snapSolidCandidates(solid, solid.vertices[0] ?? { x: 0, y: 0, z: 0 }, snap, yaw);
}

export function snapToPlane(
  geometry: BraceGeometry | PlaneGeometry,
  point: Vec3,
  snap: number,
  yaw: number
): Vec3 {
  return snapToSolid(fillSnapSolid(geometry), point, snap, yaw);
}

export function snapToPathFace(face: PathFace, point: Vec3, snap: number, yaw: number): Vec3 {
  return snapToSolid(faceSnapSolid(face), point, snap, yaw);
}

export function snapToPathGeometry(geometry: BraceGeometry, point: Vec3, snap: number, yaw: number): Vec3 {
  const solid = pathExtrudeSnapSolid(geometry);
  if (solid) return snapToSolid(solid, point, snap, yaw);
  const face = nearestPathFace(geometry, point);
  if (!face) return point;
  return snapToPathFace(face, point, snap, yaw);
}

function uniqueSorted(values: { at: number; point: Vec3 }[]): { at: number; point: Vec3 }[] {
  const sorted = [...values].sort((a, b) => a.at - b.at);
  const unique: { at: number; point: Vec3 }[] = [];
  for (const entry of sorted) {
    const last = unique[unique.length - 1];
    if (last && (entry.at - last.at < POINT_EPS || samePoint(entry.point, last.point))) continue;
    unique.push(entry);
  }
  return unique;
}

function ringCuts(ring: Vec3[], axis: GridAxis, level: number, yaw: number): Vec3[] {
  const cuts: Vec3[] = [];
  for (const point of ring) {
    if (Math.abs(gridCoords(point, yaw)[axis] - level) < POINT_EPS) pushUnique(cuts, point);
  }
  for (let index = 0; index < ring.length; index++) {
    const hit = intersectSegmentGridPlane(ring[index], ring[(index + 1) % ring.length], axis, level, yaw);
    if (hit) pushUnique(cuts, hit);
  }
  return cuts;
}

function faceCoplanarWithPlane(face: SnapFace, axis: GridAxis, level: number, yaw: number): boolean {
  return face.points.every((point) => Math.abs(gridCoords(point, yaw)[axis] - level) < 1e-4);
}

function clipFaceGridPlane(face: SnapFace, axis: GridAxis, level: number, yaw: number): PlaneSnapLine[] {
  if (faceCoplanarWithPlane(face, axis, level, yaw)) return [];
  const cuts: Vec3[] = [];
  for (const point of ringCuts(face.points, axis, level, yaw)) pushUnique(cuts, point);
  for (const hole of face.holes) {
    for (const point of ringCuts(hole, axis, level, yaw)) pushUnique(cuts, point);
  }
  if (cuts.length < 2) return [];
  const origin = cuts[0];
  const tangent = vec.normalize(
    vec.sub(
      cuts.find((point) => !samePoint(point, origin)) ?? cuts[1],
      origin
    )
  );
  if (vec.length(tangent) < 1e-9) return [];
  const ordered = uniqueSorted(cuts.map((point) => ({ at: vec.dot(vec.sub(point, origin), tangent), point })));
  const lines: PlaneSnapLine[] = [];
  for (let index = 0; index < ordered.length - 1; index++) {
    const a = ordered[index].point;
    const b = ordered[index + 1].point;
    if (samePoint(a, b)) continue;
    const mid = vec.scale(vec.add(a, b), 0.5);
    if (!pointOnSnapFace(mid, face)) continue;
    lines.push({ a, b });
  }
  return lines;
}

function clipSegmentToRadius(
  line: PlaneSnapLine,
  focus: Vec3 | undefined,
  radius: number
): PlaneSnapLine | null {
  if (!focus || !(radius > 0)) return samePoint(line.a, line.b) ? null : line;
  let t0 = 0;
  let t1 = 1;
  const dx = line.b.x - line.a.x;
  const dy = line.b.y - line.a.y;
  const dz = line.b.z - line.a.z;
  const clip = (p: number, q: number): boolean => {
    if (Math.abs(p) < POINT_EPS) return q >= -POINT_EPS;
    const t = q / p;
    if (p < 0) {
      if (t > t1) return false;
      if (t > t0) t0 = t;
    } else {
      if (t < t0) return false;
      if (t < t1) t1 = t;
    }
    return true;
  };
  if (!clip(-dx, line.a.x - (focus.x - radius)) || !clip(dx, focus.x + radius - line.a.x)) return null;
  if (!clip(-dy, line.a.y - (focus.y - radius)) || !clip(dy, focus.y + radius - line.a.y)) return null;
  if (!clip(-dz, line.a.z - (focus.z - radius)) || !clip(dz, focus.z + radius - line.a.z)) return null;
  if (t1 - t0 < 1e-9) return null;
  return {
    a: { x: line.a.x + t0 * dx, y: line.a.y + t0 * dy, z: line.a.z + t0 * dz },
    b: { x: line.a.x + t1 * dx, y: line.a.y + t1 * dy, z: line.a.z + t1 * dz },
  };
}

function nearestSnapFace(solid: SnapSolid, point: Vec3): SnapFace | null {
  let best: SnapFace | null = null;
  let bestDist = Infinity;
  for (const face of solid.faces) {
    const origin = face.points[0];
    if (!origin) continue;
    const d = vec.dot(vec.sub(point, origin), face.normal);
    const projected = vec.sub(point, vec.scale(face.normal, d));
    const on = pointOnSnapFace(projected, face);
    const dist = on
      ? Math.abs(d)
      : Math.min(
          ...face.points.map((vertex, index) => {
            const next = face.points[(index + 1) % face.points.length];
            const ab = vec.sub(next, vertex);
            const length = vec.dot(ab, ab);
            const t = length < 1e-12 ? 0 : Math.min(1, Math.max(0, vec.dot(vec.sub(projected, vertex), ab) / length));
            return vec.length(vec.sub(projected, vec.add(vertex, vec.scale(ab, t))));
          })
        );
    if (dist < bestDist) {
      best = face;
      bestDist = dist;
    }
  }
  return best;
}

function liftLine(line: PlaneSnapLine, lift: Vec3): PlaneSnapLine {
  return { a: vec.add(line.a, lift), b: vec.add(line.b, lift) };
}

function pushLine(lines: PlaneSnapLine[], line: PlaneSnapLine): void {
  if (samePoint(line.a, line.b)) return;
  lines.push(line);
}

function faceFrameLines(
  face: SnapFace,
  snap: number,
  yaw: number,
  focus: Vec3 | undefined,
  radius: number
): PlaneSnapLine[] {
  const step = snapStep(snap);
  const faceBounds = boundsOf(face.points, yaw);
  if (!faceBounds) return [];
  const window =
    focus && radius > 0
      ? clipBounds(faceBounds, expandBounds({ min: gridCoords(focus, yaw), max: gridCoords(focus, yaw) }, radius))
      : faceBounds;
  if (!window) return [];
  const traces: PlaneSnapLine[] = [];
  for (const axis of GRID_AXES) {
    for (const level of gridLevels(window.min[axis], window.max[axis], step)) {
      for (const line of clipFaceGridPlane(face, axis, level, yaw)) {
        const clipped = clipSegmentToRadius(line, focus, radius);
        if (clipped) pushLine(traces, clipped);
      }
    }
  }
  const lift = vec.scale(face.normal, STRIP_FRAME_PAD);
  const lines: PlaneSnapLine[] = [];
  for (const line of traces) {
    pushLine(lines, liftLine(line, lift));
    pushLine(lines, liftLine(line, vec.scale(lift, -1)));
  }
  return lines;
}

function solidFrameLines(
  solid: SnapSolid | null,
  snap: number,
  yaw: number,
  focus?: Vec3,
  radius = PLANE_SNAP_FRAME_RADIUS
): PlaneSnapLine[] {
  if (!solid) return [];
  const faces = focus ? [nearestSnapFace(solid, focus)].filter((face): face is SnapFace => Boolean(face)) : solid.faces;
  const lines: PlaneSnapLine[] = [];
  for (const face of faces) {
    for (const line of faceFrameLines(face, snap, yaw, focus, radius)) pushLine(lines, line);
  }
  return lines;
}

export function planeSnapFrameLines(
  geometry: PlaneGeometry,
  snap: number,
  yaw = 0,
  focus?: Vec3,
  radius = PLANE_SNAP_FRAME_RADIUS
): PlaneSnapLine[] {
  return solidFrameLines(fillSnapSolid(geometry), snap, yaw, focus, radius);
}

export function pathFaceSnapFrameLines(
  face: PathFace,
  snap: number,
  yaw = 0,
  focus?: Vec3,
  radius = PLANE_SNAP_FRAME_RADIUS
): PlaneSnapLine[] {
  return solidFrameLines(faceSnapSolid(face), snap, yaw, focus, radius);
}
